import { useState } from 'react';
import { Link } from 'react-router-dom';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import time from 'utils/time';
import GeneralItemActions from 'components/Dashboard/ItemController/GeneralItemActions';
import { CrmContactImage } from 'components/Dashboard/Influence/CrmContacts/CrmContactsTable/CrmContactItem/CrmContactItem';
import InteractionTags from './InteractionTags';

const InteractionCard = ({ item }) => {
  const {
    id,
    title,
    interactionType,
    dateTime,
    addedByUserFullName,
    description,
    crmContactIds,
    crmContactNames,
    crmContactImageIds,
    sourceCrmEmailId,
    tags,
  } = item;
  const [hoveredStakeholderIndex, setHoveredStakeholderIndex] = useState(null);
  const hasStakeholders = (crmContactIds?.length ?? 0) > 0;
  const visibleStakeholders = (crmContactIds ?? []).slice(0, 8).map((_, index) => ({
    name: crmContactNames?.[index],
    imageId: crmContactImageIds?.[index],
  }));
  const remainingAvatarsCount = (crmContactIds?.length ?? 0) - visibleStakeholders.length;
  const displayType = interactionType === 'Email' ? (sourceCrmEmailId ? 'Email campaign' : 'Email') : interactionType;

  return (
    <li className='my-2'>
      <Link to={`/people/interactions/${id}`} className='d-block px-4 py-3 text-decoration-none'>
        {/* Mobile: name + checkbox on one row, type + date on its own line below */}
        <div className='d-flex d-sm-none justify-content-between align-items-center'>
          <p className='mb-0 paragraph-p1 text-light-blue-100'>{addedByUserFullName}</p>
          <GeneralItemActions item={item} influenceItem={true} itemActionsProps={{ noDots: true }} />
        </div>
        <p
          className={`d-flex d-sm-none align-items-center ${hasStakeholders ? 'mb-2' : 'mb-1'} paragraph-p1 main-light-text`}
        >
          {displayType}
          <span className='dot'></span>
          {time(dateTime).format('DD MMM[,] YYYY')}
        </p>

        {/* Desktop: name left, type + date + actions together on the right */}
        <div className='d-none d-sm-flex justify-content-between align-items-center mb-2'>
          <p className='mb-0 paragraph-p1 text-light-blue-100'>{addedByUserFullName}</p>
          <div className='d-flex align-items-center'>
            <div className='ml-2 d-flex align-items-center'>
              <GeneralItemActions item={item} influenceItem={true} itemActionsProps={{ noDots: true }} />
            </div>
          </div>
        </div>
        <div className='mt-3'>
          {hasStakeholders && (
            <div className='d-flex align-items-center flex-shrink-0'>
              {visibleStakeholders.map((stakeholder, index) => (
                <div
                  key={index}
                  onMouseEnter={() => setHoveredStakeholderIndex(index)}
                  onMouseLeave={() => setHoveredStakeholderIndex((current) => (current === index ? null : current))}
                  data-tooltip-content={stakeholder.name}
                  data-tooltip-id='general-tooltip'
                  style={{
                    lineHeight: 0,
                    position: 'relative',
                    borderRadius: '50%',
                    boxShadow: '0 0 0 2px #fff',
                    marginLeft: index === 0 ? 0 : -8,
                    zIndex: hoveredStakeholderIndex === index ? visibleStakeholders.length + 1 : index,
                  }}
                >
                  <CrmContactImage imageId={stakeholder.imageId} size={25} />
                </div>
              ))}
              {remainingAvatarsCount > 0 && (
                <div
                  className='d-flex align-items-center justify-content-center paragraph-p3 font-weight-bold'
                  style={{
                    height: 25,
                    width: 25,
                    borderRadius: '50%',
                    marginLeft: -8,
                    boxShadow: '0 0 0 2px #fff',
                    position: 'relative',
                    backgroundColor: '#ececec',
                    color: 'rgba(0, 18, 43, 0.6)',
                    zIndex: visibleStakeholders.length,
                  }}
                >{`+${remainingAvatarsCount}`}</div>
              )}
            </div>
          )}
          <div className='mb-1 specific-top-margin-10 d-flex align-items-center' style={{ minWidth: 0 }}>
            <p
              className='mb-0 paragraph-p1 main-light-text flex-shrink-0 mr-2 d-flex align-items-center'
              style={{ height: '31px' }}
            >
              <span>{displayType}</span>
              <span className='dot specific-right-margin-6'></span>
              <span>{time(dateTime).format('DD MMM[,] YYYY')}</span>
            </p>
            {tags?.length > 0 && <InteractionTags tags={tags} />}
          </div>
          {description && (
            <div
              className='mb-0 editor-content-preview border-left-2 border-main-blue pl-3 line-clamp-1 mb-1'
              style={{ height: '21px' }}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
        </div>
      </Link>
    </li>
  );
};

const InteractionCardSkeleton = () => (
  <li className='my-2'>
    <div className='d-block px-4 py-3'>
      <div className='d-flex justify-content-between align-items-center mb-2'>
        <Skeleton height={14} width={120} />
        <div className='d-flex align-items-center'>
          <Skeleton height={24} width={80} borderRadius={50} className='mr-3' />
          <Skeleton height={14} width={100} />
        </div>
      </div>
      <Skeleton height={16} width={260} className='mb-1' />
      <Skeleton height={14} width={180} />
    </div>
  </li>
);

export const InteractionListSkeleton = () => (
  <SkeletonTheme baseColor='#f2f2f2'>
    <ul className='policy-list px-0 items-list'>
      {Array(6)
        .fill()
        .map((_, i) => (
          <InteractionCardSkeleton key={i} />
        ))}
    </ul>
  </SkeletonTheme>
);

export const InteractionScrollSkeleton = () => (
  <SkeletonTheme baseColor='#f2f2f2'>
    <InteractionCardSkeleton />
  </SkeletonTheme>
);

export default InteractionCard;
